import React, { useState, useCallback, useMemo, useRef } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useMutation, useQuery } from '@apollo/client';
import { SAVE_REPORT_DATA, GET_REPORTS, GENERATE_REPORT_SHARE_TOKEN } from '../../graphql/reports';
import { UPDATE_WORK_ORDER_EXPENSE, CREATE_WORK_ORDER_EXPENSE, DELETE_WORK_ORDER_EXPENSE } from '../../graphql/work-order-expenses-mutations';
import { UPDATE_WORK_ORDER } from '../../graphql/mutations';
import { GET_WORK_ORDER_BILLING_MARKUP, GET_WORK_ORDER_SERVICE_CHARGE, GET_SERVICE_CHARGE_HOURLY } from '../../graphql/presetExpensesAndHourlyEmployees';
import { GET_QUICKBOOKS_CONNECTION_STATUS } from '../../graphql/quickbooks';
import { exportWorkOrdersToExcel } from '../../utils/workOrderExcelExport';
import QuickBooksInvoiceModal from './QuickBooksInvoiceModal';

// Status badge colors
const STATUS_COLORS = {
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-800' },
  OPEN: { bg: 'bg-gray-100', text: 'text-gray-800' },
  IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-800' },
  CANCELED: { bg: 'bg-red-100', text: 'text-red-800' }
};

const STATUS_LABELS = {
  COMPLETED: 'Completed',
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  CANCELED: 'Canceled'
};

// Calculate billable hours helper (same logic as backend)
// First hour is free, so billable hours = total hours - 1
const calculateBillableHours = (totalMinutes) => {
  if (!totalMinutes || totalMinutes <= 0) return 0;
  const totalHours = totalMinutes / 60;
  return Math.max(0, totalHours - 1);
};

function WorkOrderReportView({
  reportData,
  startDate,
  endDate,
  savedReportId,
  savedReportName,
  onBackToSelection,
  onReportSaved,
  onRefetch,
  appliedFilters,
  isPublicView = false
}) {
  const [sortField, setSortField] = useState('submittedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedWorkOrders, setExpandedWorkOrders] = useState(new Set());

  // Edit mode controls inline expense edit inputs only. All other "edit"
  // toggles (exclude WO, unbillable expense, labor unbillable, service charge,
  // edited time) write through to entity fields immediately.
  const [isEditMode, setIsEditMode] = useState(false);
  // Local draft values for expense baseCost / billedAmount inputs. Persisted
  // on blur via UPDATE_WORK_ORDER_EXPENSE, then cleared.
  const [editedExpenses, setEditedExpenses] = useState({});
  const [editedDescriptions, setEditedDescriptions] = useState({});
  const [savingExpenses, setSavingExpenses] = useState(new Set());

  // New expense form state - one in-progress new expense per work order.
  const [newExpenses, setNewExpenses] = useState({});
  const [creatingExpenses, setCreatingExpenses] = useState(new Set());

  // Tracks rows actively saving via per-work-order mutations so we can show
  // a spinner without forcing a full refetch race.
  const [updatingWorkOrders, setUpdatingWorkOrders] = useState(new Set());

  // Share functionality state
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);

  // QuickBooks invoice modal state
  const [quickBooksModalOpen, setQuickBooksModalOpen] = useState(false);

  // Tab state for expanded work orders - tracks which tab is active per work order
  const [activeTabs, setActiveTabs] = useState({});

  // Debounce timers for free-text inputs that should auto-save to the entity
  // (currently service charge amount). One timer per work order id.
  const debounceTimersRef = useRef({});

  // Fetch billing markup percentage
  const { data: markupData } = useQuery(GET_WORK_ORDER_BILLING_MARKUP);
  const markupPercentage = markupData?.workOrderBillingMarkupPercentage || 0;

  // Fetch service charge setting
  const { data: serviceChargeData } = useQuery(GET_WORK_ORDER_SERVICE_CHARGE);
  const defaultServiceCharge = serviceChargeData?.workOrderServiceCharge || 0;

  // Fetch hourly rate setting (fallback if not in report data)
  const { data: hourlyRateData } = useQuery(GET_SERVICE_CHARGE_HOURLY);
  const settingsHourlyRate = hourlyRateData?.serviceChargeHourlyRate || 0;

  // Check QuickBooks connection status (only in non-public view)
  const { data: qbConnectionData } = useQuery(GET_QUICKBOOKS_CONNECTION_STATUS, {
    skip: isPublicView,
    fetchPolicy: 'cache-first'
  });
  const isQuickBooksConnected = qbConnectionData?.quickBooksConnectionStatus?.isConnected || false;

  // Toggle expand/collapse for a work order
  const toggleExpand = useCallback((workOrderId) => {
    setExpandedWorkOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(workOrderId)) {
        newSet.delete(workOrderId);
      } else {
        newSet.add(workOrderId);
      }
      return newSet;
    });
  }, []);

  const expandWorkOrders = useCallback((workOrderIds) => {
    setExpandedWorkOrders(prev => {
      const next = new Set(prev);
      workOrderIds.forEach(id => next.add(id));
      return next;
    });
  }, []);

  const collapseWorkOrders = useCallback((workOrderIds) => {
    setExpandedWorkOrders(prev => {
      const next = new Set(prev);
      workOrderIds.forEach(id => next.delete(id));
      return next;
    });
  }, []);

  // Handle tab change for a work order
  const handleTabChange = useCallback((workOrderId, tab) => {
    setActiveTabs(prev => ({
      ...prev,
      [workOrderId]: tab
    }));
  }, []);

  // Get active tab for a work order (defaults to 'details')
  const getActiveTab = useCallback((workOrderId) => {
    return activeTabs[workOrderId] || 'details';
  }, [activeTabs]);

  // Check if work order has expenses
  const hasExpenses = useCallback((wo) => {
    return wo.expenses && wo.expenses.length > 0;
  }, []);

  // Get the current value for an expense field (in-progress edit or persisted entity value)
  const getExpenseValue = useCallback((expense, field) => {
    if (editedExpenses[expense.id]?.[field] !== undefined) {
      return editedExpenses[expense.id][field];
    }
    return expense[field];
  }, [editedExpenses]);

  // Check if an expense has unsaved local edits
  const isExpenseModified = useCallback((expense) => {
    const edited = editedExpenses[expense.id];
    if (!edited) return false;

    const titleChanged = edited.title !== undefined &&
      (edited.title || '') !== (expense.title || '');
    const baseCostChanged = edited.baseCost !== undefined &&
      parseFloat(edited.baseCost || 0) !== parseFloat(expense.baseCost || 0);
    const billedAmountChanged = edited.billedAmount !== undefined &&
      parseFloat(edited.billedAmount || 0) !== parseFloat(expense.billedAmount || 0);

    return titleChanged || baseCostChanged || billedAmountChanged;
  }, [editedExpenses]);

  // Handle expense field change
  const handleExpenseChange = useCallback((expense, field, value) => {
    const expenseId = expense.id;
    setEditedExpenses(prev => ({
      ...prev,
      [expenseId]: {
        ...prev[expenseId],
        [field]: value
      }
    }));
  }, []);

  // Decorate live entity rows with derived display values. All persisted
  // state (isExcludedFromInvoicing, isLaborUnbillable, isServiceChargeEnabled,
  // serviceChargeAmount, editedTotalTimeMinutes, expense.isBillable) is read
  // straight from the work order/expense rows returned by the live query.
  const workOrdersWithUpdates = useMemo(() => {
    if (!reportData?.workOrders) return [];

    return reportData.workOrders.map(wo => {
      const expenses = wo.expenses || [];

      const expenseBaseCost = expenses.reduce((sum, exp) => sum + (parseFloat(exp.baseCost) || 0), 0);
      const expenseBilledAmount = expenses.reduce((sum, exp) => {
        if (exp.isBillable === false) return sum;
        const billedAmount = parseFloat(exp.billedAmount) || 0;
        const baseCost = parseFloat(exp.baseCost) || 0;
        if (billedAmount > 0) return sum + billedAmount;
        if (baseCost > 0 && markupPercentage > 0) return sum + (baseCost * (1 + markupPercentage / 100));
        if (baseCost > 0) return sum + baseCost;
        return sum;
      }, 0);

      // Backend already returns billableHours/laborCharge respecting the
      // edited-time override and isLaborUnbillable; we just expose the
      // ergonomics flag for the JSX (isLaborBillable).
      const isLaborUnbillable = !!wo.isLaborUnbillable;
      const isLaborBillable = !isLaborUnbillable;
      const laborCharge = parseFloat(wo.laborCharge) || 0;
      const billableLaborCharge = isLaborBillable ? laborCharge : 0;

      const hasServiceCharge = !!wo.isServiceChargeEnabled;
      const serviceCharge = hasServiceCharge
        ? (wo.serviceChargeAmount !== null && wo.serviceChargeAmount !== undefined
            ? parseFloat(wo.serviceChargeAmount) || 0
            : defaultServiceCharge)
        : 0;

      const totalBilledAmount = expenseBilledAmount + billableLaborCharge + serviceCharge;

      return {
        ...wo,
        expenses,
        timeEntries: wo.timeEntries || [],
        totalTimeWorkedMinutes: wo.totalTimeWorkedMinutes || 0,
        billableHours: parseFloat(wo.billableHours) || 0,
        totalBaseCost: expenseBaseCost,
        totalBilledAmount,
        totalProfit: totalBilledAmount - expenseBaseCost,
        laborCharge,
        isLaborBillable,
        isLaborUnbillable,
        hasServiceCharge,
        serviceCharge,
        isExcludedFromInvoicing: !!wo.isExcludedFromInvoicing,
        isInvoiced: !!wo.quickbooksInvoiceId || !!wo.invoicedAt,
        quickbooksInvoiceNumber: wo.quickbooksInvoiceNumber
      };
    });
  }, [reportData?.workOrders, defaultServiceCharge, markupPercentage]);

  const workOrders = workOrdersWithUpdates;

  // Active work orders = all work orders minus those marked excluded from
  // invoicing on the entity. Excluded rows are hidden from the report output
  // and dropped from monetary totals.
  const activeWorkOrders = useMemo(() => {
    return workOrders.filter(wo => !wo.isExcludedFromInvoicing);
  }, [workOrders]);

  const excludedCount = useMemo(() => {
    return workOrders.filter(wo => wo.isExcludedFromInvoicing).length;
  }, [workOrders]);

  // Calculate updated summary - always recalculate based on active (non-excluded) work orders
  const summary = useMemo(() => {
    const baseSummary = reportData?.summary || {};
    
    // Recalculate grand totals based on active work orders only
    const grandTotalBaseCost = activeWorkOrders.reduce((sum, wo) => sum + (wo.totalBaseCost || 0), 0);
    
    // Calculate approved/billable labor only
    const approvedLaborCharge = activeWorkOrders.reduce((sum, wo) => sum + (wo.isLaborBillable ? (wo.laborCharge || 0) : 0), 0);
    
    // Calculate total service charges
    const totalServiceCharges = activeWorkOrders.reduce((sum, wo) => sum + (wo.serviceCharge || 0), 0);
    
    // wo.totalBilledAmount already includes expenses + billable labor + service charges
    const grandTotalBilledAmount = activeWorkOrders.reduce((sum, wo) => sum + (wo.totalBilledAmount || 0), 0);
    const grandTotalProfit = grandTotalBilledAmount - grandTotalBaseCost;
    
    // Calculate labor totals (all labor, for display)
    const totalLaborCharge = activeWorkOrders.reduce((sum, wo) => sum + (wo.laborCharge || 0), 0);
    const totalTimeWorkedMinutes = activeWorkOrders.reduce((sum, wo) => sum + (wo.totalTimeWorkedMinutes || 0), 0);
    const totalBillableHours = activeWorkOrders.reduce((sum, wo) => sum + (wo.billableHours || 0), 0);
    
    // Total cost contribution of expenses explicitly flagged not-billable.
    const totalUnbilledExpenses = activeWorkOrders.reduce((sum, wo) => {
      const unbilledExpenseCost = (wo.expenses || []).reduce((expSum, exp) => {
        if (exp.isBillable !== false) return expSum;
        const baseCost = parseFloat(exp.baseCost) || 0;
        return expSum + baseCost;
      }, 0);
      return sum + unbilledExpenseCost;
    }, 0);

    // Total billed contribution of expenses still marked billable.
    const totalBilledExpenses = activeWorkOrders.reduce((sum, wo) => {
      const expenseBilled = (wo.expenses || []).reduce((expSum, exp) => {
        if (exp.isBillable === false) return expSum;
        const billedAmount = parseFloat(exp.billedAmount) || 0;
        const baseCost = parseFloat(exp.baseCost) || 0;
        if (billedAmount > 0) return expSum + billedAmount;
        if (baseCost > 0 && markupPercentage > 0) return expSum + (baseCost * (1 + markupPercentage / 100));
        if (baseCost > 0) return expSum + baseCost;
        return expSum;
      }, 0);
      return sum + expenseBilled;
    }, 0);
    
    // Calculate unbillable labor (those explicitly marked as unbillable)
    const unbilledLabor = totalLaborCharge - approvedLaborCharge;
    
    // Calculate totals
    const totalUnbilled = totalUnbilledExpenses + unbilledLabor;
    const totalBilled = totalBilledExpenses + totalServiceCharges + approvedLaborCharge;

    // Recalculate status counts based on active work orders
    const totalWorkOrders = activeWorkOrders.length;
    const completedCount = activeWorkOrders.filter(wo => wo.status === 'COMPLETED').length;
    const openCount = activeWorkOrders.filter(wo => wo.status === 'OPEN').length;
    const inProgressCount = activeWorkOrders.filter(wo => wo.status === 'IN_PROGRESS').length;
    const canceledCount = activeWorkOrders.filter(wo => wo.status === 'CANCELED').length;
    const urgentCount = activeWorkOrders.filter(wo => wo.isUrgent).length;
    const feedbackCount = activeWorkOrders.filter(wo => wo.isFeedback).length;
    
    return {
      ...baseSummary,
      totalWorkOrders,
      completedCount,
      openCount,
      inProgressCount,
      canceledCount,
      urgentCount,
      feedbackCount,
      grandTotalBaseCost,
      grandTotalBilledAmount,
      totalServiceCharges,
      grandTotalProfit,
      totalLaborCharge,
      totalTimeWorkedMinutes,
      totalBillableHours,
      approvedLaborCharge,
      totalUnbilledExpenses,
      totalBilledExpenses,
      totalUnbilled,
      totalBilled,
      serviceChargeHourlyRate: baseSummary.serviceChargeHourlyRate || settingsHourlyRate || 0
    };
  }, [reportData?.summary, activeWorkOrders, settingsHourlyRate, markupPercentage]);
  const appliedAreas = reportData?.appliedAreas || [];
  const appliedDepartments = reportData?.appliedDepartments || [];
  const appliedStatuses = reportData?.appliedStatuses || [];
  const appliedCustomerIds = reportData?.appliedCustomerIds || [];
  const appliedPropertyIds = reportData?.appliedPropertyIds || [];

  const [saveReportData, { loading: isSaving }] = useMutation(SAVE_REPORT_DATA, {
    refetchQueries: [{ query: GET_REPORTS }],
    onCompleted: (data) => {
      if (data.saveReportData) {
        toast.success('Report saved');
        if (onReportSaved) {
          onReportSaved(data.saveReportData);
        }
      }
    },
    onError: (error) => {
      toast.error(`Failed to save report: ${error.message}`);
    }
  });

  // Share token mutation
  const [generateReportShareToken] = useMutation(GENERATE_REPORT_SHARE_TOKEN);

  const [updateExpense] = useMutation(UPDATE_WORK_ORDER_EXPENSE, {
    onError: (error) => {
      toast.error(`Failed to update expense: ${error.message}`);
    }
  });

  const [createExpense] = useMutation(CREATE_WORK_ORDER_EXPENSE, {
    onError: (error) => {
      toast.error(`Failed to create expense: ${error.message}`);
    }
  });

  const [deleteExpense] = useMutation(DELETE_WORK_ORDER_EXPENSE, {
    onError: (error) => {
      toast.error(`Failed to delete expense: ${error.message}`);
    }
  });

  // Delete an expense after explicit confirmation. Refreshes the report so
  // totals (and the work order's expense list) reflect the removal.
  const handleDeleteExpense = useCallback(async (expense) => {
    const confirmed = window.confirm(`Delete expense "${expense.title}"? This cannot be undone.`);
    if (!confirmed) return;
    setSavingExpenses(prev => new Set(prev).add(expense.id));
    try {
      await deleteExpense({ variables: { id: expense.id } });
      if (onRefetch) await onRefetch();
      toast.success('Expense deleted');
      setEditedExpenses(prev => {
        const next = { ...prev };
        delete next[expense.id];
        return next;
      });
    } catch (err) {
      // surfaced via mutation onError
    } finally {
      setSavingExpenses(prev => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
    }
  }, [deleteExpense, onRefetch]);

  // Direct write-through to the work order entity for report adjustments.
  const [updateWorkOrder] = useMutation(UPDATE_WORK_ORDER, {
    onError: (error) => {
      toast.error(`Failed to update work order: ${error.message}`);
    }
  });

  // Mark a work order as updating, run the mutation, refetch the report
  // to re-pull totals and any derived backend values, then clear the marker.
  //
  // UpdateWorkOrderInput requires `id` as a non-null field inside the input
  // (in addition to the separate `$id` GraphQL variable on the resolver), so
  // every call site has it merged in automatically here.
  const persistWorkOrderField = useCallback(async (workOrderId, input, successMessage) => {
    setUpdatingWorkOrders(prev => new Set(prev).add(workOrderId));
    try {
      await updateWorkOrder({
        variables: {
          id: workOrderId,
          input: { id: workOrderId, ...input }
        }
      });
      if (onRefetch) await onRefetch();
      if (successMessage) toast.success(successMessage);
    } catch (err) {
      // Surfaced via mutation onError
    } finally {
      setUpdatingWorkOrders(prev => {
        const next = new Set(prev);
        next.delete(workOrderId);
        return next;
      });
    }
  }, [updateWorkOrder, onRefetch]);

  // Live-update local edit state for a work order's description.
  const handleDescriptionChange = useCallback((workOrderId, value) => {
    setEditedDescriptions(prev => ({ ...prev, [workOrderId]: value }));
  }, []);

  // Persist a description change on blur. No-ops if unchanged or empty
  // (description is required at the DB level).
  const handleDescriptionBlur = useCallback(async (workOrder) => {
    const pending = editedDescriptions[workOrder.id];
    if (pending === undefined) return;
    const trimmed = (pending || '').trim();
    if (trimmed === (workOrder.description || '').trim()) {
      setEditedDescriptions(prev => {
        const next = { ...prev };
        delete next[workOrder.id];
        return next;
      });
      return;
    }
    if (trimmed === '') {
      toast.error('Description cannot be empty');
      return;
    }
    await persistWorkOrderField(workOrder.id, { description: trimmed }, 'Description updated');
    setEditedDescriptions(prev => {
      const next = { ...prev };
      delete next[workOrder.id];
      return next;
    });
  }, [editedDescriptions, persistWorkOrderField]);

  // Flush any pending description edits before exiting edit mode.
  const saveAllPendingDescriptions = useCallback(async () => {
    const ids = Object.keys(editedDescriptions);
    if (ids.length === 0) return;
    for (const wo of workOrders) {
      if (editedDescriptions[wo.id] !== undefined) {
        await handleDescriptionBlur(wo);
      }
    }
  }, [editedDescriptions, workOrders, handleDescriptionBlur]);

  // Toggle labor unbillable - persists on the work order entity.
  const handleToggleLaborBillable = useCallback((workOrder) => {
    const next = !workOrder.isLaborUnbillable;
    persistWorkOrderField(
      workOrder.id,
      { isLaborUnbillable: next },
      next ? 'Labor marked as unbillable' : 'Labor marked as billable'
    );
  }, [persistWorkOrderField]);

  // Toggle expense billable flag on the entity, refetch to recompute totals.
  const handleToggleExpenseUnbillable = useCallback(async (expense) => {
    const next = expense.isBillable === false;
    try {
      await updateExpense({
        variables: {
          id: expense.id,
          input: { isBillable: next }
        }
      });
      if (onRefetch) await onRefetch();
      toast.success(next ? 'Expense marked as billable' : 'Expense marked as unbillable');
    } catch (err) {
      // surfaced via mutation onError
    }
  }, [updateExpense, onRefetch]);

  // Edited total time minutes - persisted directly to the work order entity.
  const handleTimeEdit = useCallback((workOrderId, hours, minutes) => {
    const totalMinutes = (parseInt(hours) || 0) * 60 + (parseInt(minutes) || 0);
    persistWorkOrderField(workOrderId, { editedTotalTimeMinutes: totalMinutes });
  }, [persistWorkOrderField]);

  // Toggle service charge on the work order entity.
  const handleToggleServiceCharge = useCallback((workOrder) => {
    const enabling = !workOrder.isServiceChargeEnabled;
    const input = enabling
      ? { isServiceChargeEnabled: true }
      : { isServiceChargeEnabled: false, serviceChargeAmount: null };
    const formattedDefault = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(defaultServiceCharge || 0));
    persistWorkOrderField(
      workOrder.id,
      input,
      enabling ? `Service charge of ${formattedDefault} added` : 'Service charge removed'
    );
  }, [persistWorkOrderField, defaultServiceCharge]);

  // Debounced commit for a free-text service charge amount input.
  const handleServiceChargeChange = useCallback((workOrderId, value) => {
    if (debounceTimersRef.current[workOrderId]) {
      clearTimeout(debounceTimersRef.current[workOrderId]);
    }
    debounceTimersRef.current[workOrderId] = setTimeout(() => {
      const parsed = value === '' || value === null ? null : parseFloat(value);
      persistWorkOrderField(workOrderId, {
        serviceChargeAmount: Number.isFinite(parsed) ? parsed : null
      });
      delete debounceTimersRef.current[workOrderId];
    }, 600);
  }, [persistWorkOrderField]);

  // Handle new expense field changes
  const handleNewExpenseChange = useCallback((workOrderId, field, value) => {
    setNewExpenses(prev => ({
      ...prev,
      [workOrderId]: {
        ...prev[workOrderId],
        [field]: value
      }
    }));
  }, []);

  // Create new expense via mutation, then refetch the live report.
  const handleCreateExpense = useCallback(async (workOrderId) => {
    const newExpense = newExpenses[workOrderId];
    if (!newExpense || !newExpense.title || !newExpense.title.trim()) {
      toast.error('Please enter an expense title');
      return;
    }

    setCreatingExpenses(prev => new Set(prev).add(workOrderId));

    try {
      const baseCost = newExpense.baseCost ? parseFloat(newExpense.baseCost) : null;
      const billedAmount = newExpense.billedAmount ? parseFloat(newExpense.billedAmount) : null;

      await createExpense({
        variables: {
          input: {
            workOrderId,
            title: newExpense.title.trim(),
            baseCost: baseCost && baseCost > 0 ? baseCost : null,
            billedAmount: billedAmount && billedAmount > 0 ? billedAmount : null
          }
        }
      });

      if (onRefetch) await onRefetch();

      setNewExpenses(prev => {
        const updated = { ...prev };
        delete updated[workOrderId];
        return updated;
      });

      toast.success('Expense added');
    } catch (error) {
      // surfaced via mutation onError
    } finally {
      setCreatingExpenses(prev => {
        const newSet = new Set(prev);
        newSet.delete(workOrderId);
        return newSet;
      });
    }
  }, [newExpenses, createExpense, onRefetch]);

  // Save an individual expense (called on blur). Persists straight to the
  // entity and refreshes the live report so totals refresh.
  const handleSaveExpense = useCallback(async (expense, showToast = false) => {
    const edited = editedExpenses[expense.id];
    if (!edited) return;

    const newTitle = edited.title !== undefined
      ? (edited.title || '').trim()
      : expense.title;
    const newBaseCost = edited.baseCost !== undefined
      ? (edited.baseCost === '' ? null : parseFloat(edited.baseCost))
      : expense.baseCost;
    const newBilledAmount = edited.billedAmount !== undefined
      ? (edited.billedAmount === '' ? null : parseFloat(edited.billedAmount))
      : expense.billedAmount;

    if (
      newTitle === expense.title &&
      newBaseCost === expense.baseCost &&
      newBilledAmount === expense.billedAmount
    ) {
      setEditedExpenses(prev => {
        const newState = { ...prev };
        delete newState[expense.id];
        return newState;
      });
      return;
    }

    // Title is required by the API - if the user cleared it, abort the save
    // and surface a toast rather than silently dropping the change.
    if (edited.title !== undefined && newTitle === '') {
      toast.error('Expense title cannot be empty');
      return;
    }

    setSavingExpenses(prev => new Set(prev).add(expense.id));

    try {
      const input = {};
      if (edited.title !== undefined) input.title = newTitle;
      if (edited.baseCost !== undefined) input.baseCost = newBaseCost;
      if (edited.billedAmount !== undefined) input.billedAmount = newBilledAmount;

      await updateExpense({
        variables: { id: expense.id, input }
      });
      if (onRefetch) await onRefetch();

      setEditedExpenses(prev => {
        const newState = { ...prev };
        delete newState[expense.id];
        return newState;
      });

      if (showToast) toast.success('Expense updated');
    } catch (error) {
      // surfaced via mutation onError
    } finally {
      setSavingExpenses(prev => {
        const newSet = new Set(prev);
        newSet.delete(expense.id);
        return newSet;
      });
    }
  }, [editedExpenses, updateExpense, onRefetch]);

  // Save all pending expense edits before exiting edit mode.
  const saveAllPendingExpenses = useCallback(async () => {
    if (Object.keys(editedExpenses).length === 0) return true;

    const expensesToSave = [];
    for (const wo of workOrders) {
      if (!wo.expenses) continue;
      for (const exp of wo.expenses) {
        if (editedExpenses[exp.id]) expensesToSave.push(exp);
      }
    }

    for (const expense of expensesToSave) {
      await handleSaveExpense(expense, false);
    }

    return true;
  }, [editedExpenses, workOrders, handleSaveExpense]);

  // Handle blur on expense input - auto-save with a short tab delay.
  const handleExpenseBlur = useCallback((expense) => {
    setTimeout(() => {
      if (editedExpenses[expense.id]) {
        handleSaveExpense(expense, false);
      }
    }, 150);
  }, [editedExpenses, handleSaveExpense]);

  const handleToggleEditMode = useCallback(async () => {
    if (isEditMode) {
      await saveAllPendingExpenses();
      await saveAllPendingDescriptions();
      setEditedExpenses({});
      setEditedDescriptions({});
      setNewExpenses({});
    }
    setIsEditMode(prev => !prev);
  }, [isEditMode, saveAllPendingExpenses, saveAllPendingDescriptions]);

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number(amount));
  };

  // Print and Share: triggers a backend snapshot refresh on the single
  // share token tied to this saved query, then opens the public URL.
  // The shared URL stays stable - clicking this button again replaces the
  // frozen snapshot with current numbers for the same URL.
  const handlePrintAndShare = async () => {
    if (!savedReportId) {
      toast.error('Save the report first to enable sharing');
      return;
    }

    try {
      setIsGeneratingToken(true);
      const result = await generateReportShareToken({
        variables: { input: { reportId: savedReportId } }
      });

      if (result.data?.generateReportShareToken) {
        const { token } = result.data.generateReportShareToken;
        const url = `${window.location.origin}/shared/reports/${savedReportId}?token=${token}`;
        window.open(url, '_blank');
      }
    } catch (error) {
      toast.error('Failed to refresh shared snapshot');
    } finally {
      setIsGeneratingToken(false);
    }
  };

  // Handle QuickBooks estimate creation success
  const handleQuickBooksInvoiceCreated = useCallback((result) => {
    // The modal already shows the success message
    // Here we could refresh data if needed, but the work order estimate status
    // will be updated on next report generation
    
    // If there are created estimates, show links to view them
    if (result.invoices && result.invoices.length > 0) {
      result.invoices.forEach(invoice => {
        if (invoice.quickbooksUrl) {
          console.log(`Estimate ${invoice.quickbooksInvoiceNumber} created: ${invoice.quickbooksUrl}`);
        }
      });
    }
  }, []);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Toggle exclusion of a work order. Persists on the work order entity so
  // the choice survives re-runs of the saved query.
  const toggleExcludeWorkOrder = useCallback(async (workOrder, e) => {
    if (e) e.stopPropagation();
    const next = !workOrder.isExcludedFromInvoicing;
    await persistWorkOrderField(
      workOrder.id,
      { isExcludedFromInvoicing: next },
      next ? 'Work order excluded from report' : 'Work order restored to report'
    );
  }, [persistWorkOrderField]);

  // Filter and sort work orders. Rows removed from the report stay persisted
  // as excluded on the work order entity, but are hidden from this output.
  const filteredAndSortedWorkOrders = React.useMemo(() => {
    let filtered = workOrders.filter(wo => !wo.isExcludedFromInvoicing);

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(wo =>
        wo.description?.toLowerCase().includes(search) ||
        wo.propertyName?.toLowerCase().includes(search) ||
        wo.assignedToName?.toLowerCase().includes(search) ||
        wo.propertyCode?.toLowerCase().includes(search)
      );
    }

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (sortField.includes('At') || sortField.includes('Date')) {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  }, [workOrders, searchTerm, sortField, sortDirection]);

  const filteredWorkOrderIds = useMemo(() => (
    filteredAndSortedWorkOrders.map(wo => wo.id)
  ), [filteredAndSortedWorkOrders]);

  const areAllVisibleWorkOrdersExpanded = useMemo(() => (
    filteredWorkOrderIds.length > 0 && filteredWorkOrderIds.every(id => expandedWorkOrders.has(id))
  ), [filteredWorkOrderIds, expandedWorkOrders]);

  const handleToggleAllAccordions = useCallback(() => {
    if (areAllVisibleWorkOrdersExpanded) {
      collapseWorkOrders(filteredWorkOrderIds);
    } else {
      expandWorkOrders(filteredWorkOrderIds);
    }
  }, [
    areAllVisibleWorkOrdersExpanded,
    collapseWorkOrders,
    expandWorkOrders,
    filteredWorkOrderIds
  ]);

  // Restore all currently-excluded work orders in one batch.
  const handleRestoreAllExcluded = useCallback(async () => {
    const excluded = workOrders.filter(wo => wo.isExcludedFromInvoicing);
    for (const wo of excluded) {
      await persistWorkOrderField(wo.id, { isExcludedFromInvoicing: false });
    }
    if (excluded.length > 0) toast.success('All excluded work orders restored');
  }, [workOrders, persistWorkOrderField]);

  // Save / update the underlying saved query (filters only - no snapshot).
  // All numerical adjustments live on the work order entities themselves
  // and re-run live on every load of the saved query.
  const handleSaveQuery = async () => {
    await saveAllPendingExpenses();

    const startLabel = format(new Date(startDate + 'T00:00:00'), 'MMM dd, yyyy');
    const endLabel = format(new Date(endDate + 'T00:00:00'), 'MMM dd, yyyy');
    const reportName = savedReportName || `Work Orders Report - ${startLabel} to ${endLabel}`;

    const queryBlob = {
      filters: appliedFilters || {
        workOrderStatuses: reportData?.appliedStatuses || [],
        departments: reportData?.appliedDepartments || [],
        areas: reportData?.appliedAreas || [],
        quickbooksCustomerIds: reportData?.appliedCustomerIds || [],
        propertyIds: reportData?.appliedPropertyIds || []
      }
    };

    const saveInput = {
      name: reportName,
      existingReportId: savedReportId || null,
      reportDataJson: JSON.stringify(queryBlob),
      type: 'WORK_ORDERS',
      startDate,
      endDate,
      roleFilter: null,
      totalPayout: 0,
      totalCost: 0,
      totalExpenses: 0,
      finalTotal: 0,
      jobsCount: summary.totalWorkOrders || 0
    };

    await saveReportData({ variables: { input: saveInput } });
  };

  // Build export-ready report data - excluded WOs are already filtered out
  // of activeWorkOrders via the entity flag.
  const exportReportData = useMemo(() => ({
    ...reportData,
    workOrders: activeWorkOrders,
    summary
  }), [reportData, activeWorkOrders, summary]);

  const handleExportExcel = async () => {
    try {
      await exportWorkOrdersToExcel(exportReportData, { startDate, endDate, appliedAreas, appliedDepartments, appliedStatuses });
      toast.success('Report exported to Excel');
    } catch (error) {
      toast.error(`Excel export failed: ${error.message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Print Styles - Always expand work orders when printing */}
      <style>
        {`
          @media print {
            .print-expand { display: table-row !important; }
            .no-print { display: none !important; }
            .print-hide-column { display: none !important; }
          }
          @media screen {
            .print-only { display: none !important; }
          }
        `}
      </style>
      
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            {!isPublicView && (
            <button
              onClick={onBackToSelection}
              className="text-purple-600 hover:text-purple-800 flex items-center space-x-2 mb-2"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Reports</span>
            </button>
            )}
            <h2 className="text-2xl font-bold text-gray-900">
              Work Orders Report
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {format(new Date(startDate + 'T00:00:00'), 'MMM dd, yyyy')} - {format(new Date(endDate + 'T00:00:00'), 'MMM dd, yyyy')}
            </p>
          </div>
          
          {/* Actions Container */}
          <div className="flex flex-col gap-2">
            {/* Primary Actions Row */}
            <div className="flex space-x-3 justify-end">
              {/* Edit Expenses - only show in non-public view */}
              {!isPublicView && (
              <button
                onClick={handleToggleEditMode}
                className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${
                  isEditMode 
                    ? 'border-green-500 text-green-700 bg-green-50 hover:bg-green-100' 
                    : 'border-purple-300 text-purple-700 bg-white hover:bg-purple-50'
                }`}
              >
                {isEditMode ? (
                  <>
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Done Editing
                  </>
                ) : (
                  <>
                    <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Expenses
                  </>
                )}
              </button>
              )}
              
              {/* Save - only shown in non-public view. Persists the filters
                  and date range. Edits inside the report (billable toggles,
                  service charges, exclusions, time overrides) save directly
                  to the work order entities and don't need this button. */}
              {!isPublicView && (
              <button
                onClick={handleSaveQuery}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                title={savedReportId ? 'Update saved report filters' : 'Save report filters for re-use'}
              >
                {isSaving ? 'Saving...' : savedReportId ? 'Update Saved Report' : 'Save Report'}
              </button>
              )}
            </div>
            
            {/* Secondary Actions Row */}
            <div className="flex space-x-2 justify-end">
              {/* Export XLS */}
              <button
                onClick={handleExportExcel}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-600 bg-white hover:bg-gray-50"
              >
                <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export XLS
              </button>
              
              {/* Send to QuickBooks - only show in non-public view when connected */}
              {!isPublicView && isQuickBooksConnected && (
                <button
                  onClick={() => setQuickBooksModalOpen(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-green-300 rounded text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100"
                  aria-label="Send to QuickBooks"
                  tabIndex={0}
                >
                  <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  QuickBooks
                </button>
              )}
              
              {/* Print & Share - only show in non-public view. Refreshes the
                  frozen snapshot tied to this saved query and opens the
                  public URL (URL is stable, snapshot is what changes). */}
              {!isPublicView && (
                <button
                  onClick={handlePrintAndShare}
                  disabled={isGeneratingToken || !savedReportId}
                  className="inline-flex items-center px-3 py-1.5 border border-blue-300 rounded text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!savedReportId ? 'Save the report first to enable sharing' : 'Refreshes the shared snapshot and opens it in a new tab'}
                >
                  {isGeneratingToken ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      ...
                    </>
                  ) : !savedReportId ? (
                    <>
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      Save First
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Print & Share
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Applied Filters */}
        {(appliedStatuses.length > 0 || appliedDepartments.length > 0 || appliedAreas.length > 0 || appliedCustomerIds.length > 0 || appliedPropertyIds.length > 0) && (
          <div className="border-t pt-4">
            <h4 className="text-xs font-medium text-gray-700 mb-2">Applied Filters:</h4>
            <div className="flex flex-wrap gap-2">
              {appliedStatuses.map(status => (
                <span key={status} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status]?.bg} ${STATUS_COLORS[status]?.text}`}>
                  {STATUS_LABELS[status]}
                </span>
              ))}
              {appliedDepartments.map(dept => (
                <span key={dept} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {dept}
                </span>
              ))}
              {appliedAreas.map(area => (
                <span key={area} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {area}
                </span>
              ))}
              {appliedCustomerIds.length > 0 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  👥 {appliedCustomerIds.length} customer{appliedCustomerIds.length !== 1 ? 's' : ''} filtered
                </span>
              )}
              {appliedPropertyIds.length > 0 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  🏠 {appliedPropertyIds.length} propert{appliedPropertyIds.length !== 1 ? 'ies' : 'y'} filtered
                </span>
              )}
            </div>
          </div>
        )}

        {/* Edit Mode Banner */}
        {isEditMode && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <svg className="h-5 w-5 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <p className="text-sm font-medium text-purple-800">Edit Mode Active</p>
                <p className="text-xs text-purple-600">All items are billable by default. Mark individual expenses or labor as unbillable to exclude them. Changes save automatically to each work order.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Work Orders Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Work Orders ({filteredAndSortedWorkOrders.length})
              {excludedCount > 0 && (
                <span className="ml-2 text-sm font-normal text-red-600">
                  ({excludedCount} excluded)
                </span>
              )}
            </h3>
            <div className="flex items-center space-x-2">
              {filteredAndSortedWorkOrders.length > 0 && (
                <button
                  onClick={handleToggleAllAccordions}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100"
                  aria-label={areAllVisibleWorkOrdersExpanded ? 'Collapse all work order details' : 'Expand all work order details'}
                  tabIndex={0}
                >
                  <svg
                    className={`w-3 h-3 mr-1 transform transition-transform ${areAllVisibleWorkOrdersExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  {areAllVisibleWorkOrdersExpanded ? 'Collapse All' : 'Expand All'}
                </button>
              )}
              {excludedCount > 0 && !isPublicView && (
                <button
                  onClick={handleRestoreAllExcluded}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                  aria-label="Restore all excluded work orders"
                  tabIndex={0}
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Restore All
                </button>
              )}
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10 print-hide-column">
                  {/* Expand column - hidden when printing */}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('propertyName')}
                >
                  Property {sortField === 'propertyName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned To
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalBaseCost')}
                >
                  Base Cost {sortField === 'totalBaseCost' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalBilledAmount')}
                >
                  Billed {sortField === 'totalBilledAmount' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('submittedAt')}
                >
                  Submitted {sortField === 'submittedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedWorkOrders.map(wo => {
                const isExpanded = expandedWorkOrders.has(wo.id);
                const woHasExpenses = hasExpenses(wo);
                
                return (
                  <React.Fragment key={wo.id}>
                    {/* Main work order row - all rows are expandable */}
                    <tr 
                      className={`hover:bg-gray-50 cursor-pointer ${isExpanded ? 'bg-purple-50' : ''}`}
                      onClick={() => toggleExpand(wo.id)}
                    >
                      <td className="px-3 py-4 whitespace-nowrap print-hide-column">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(wo.id);
                            }}
                            className="text-purple-600 hover:text-purple-800 focus:outline-none"
                            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                            tabIndex={0}
                          >
                            <svg
                              className={`h-5 w-5 transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {!isPublicView && (
                            <button
                              onClick={(e) => toggleExcludeWorkOrder(wo, e)}
                              className="text-red-400 hover:text-red-600 focus:outline-none ml-1"
                              aria-label="Remove from report"
                              title="Remove from report"
                              tabIndex={0}
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{wo.propertyName}</div>
                        <div className="text-xs text-gray-500">{wo.propertyArea}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {wo.isUrgent && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                              🔥 Urgent
                            </span>
                          )}
                          {wo.isFeedback && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              💬 Feedback
                            </span>
                          )}
                          {woHasExpenses && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                              💰 {wo.expenses.length} expense{wo.expenses.length > 1 ? 's' : ''}
                            </span>
                          )}
                          {wo.isInvoiced && (
                            <span 
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800"
                              title={wo.quickbooksInvoiceNumber ? `Estimate #${wo.quickbooksInvoiceNumber}` : 'Sent to QuickBooks'}
                            >
                              ✓ Sent to QB
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[wo.status]?.bg} ${STATUS_COLORS[wo.status]?.text}`}>
                          {STATUS_LABELS[wo.status]}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {wo.assignedDepartment}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {wo.assignedToName || 'Unassigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(wo.totalBaseCost)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(wo.totalBilledAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(wo.submittedAt), 'MMM dd, yyyy')}
                      </td>
                    </tr>
                    
                    {/* Expanded details row - shows description and expenses */}
                    {/* Always render for print, but hide on screen when collapsed */}
                    <tr 
                      className={`bg-gray-50 ${isExpanded ? '' : 'hidden'} print-expand`}
                      style={{ pageBreakInside: 'avoid' }}
                    >
                      <td colSpan={8} className="px-6 py-4">
                          <div className="ml-8 border-l-2 border-purple-300 pl-4">
                            {/* Tabbed Interface for Details and Comments */}
                            <div className="mb-4">
                              {/* Tab Headers */}
                              <div className="flex border-b border-gray-200 mb-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTabChange(wo.id, 'details');
                                  }}
                                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                                    getActiveTab(wo.id) === 'details'
                                      ? 'text-purple-700 border-b-2 border-purple-600'
                                      : 'text-gray-500 hover:text-gray-700'
                                  }`}
                                  tabIndex={0}
                                  aria-label="View details tab"
                                >
                                  Details
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTabChange(wo.id, 'comments');
                                  }}
                                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                                    getActiveTab(wo.id) === 'comments'
                                      ? 'text-purple-700 border-b-2 border-purple-600'
                                      : 'text-gray-500 hover:text-gray-700'
                                  }`}
                                  tabIndex={0}
                                  aria-label="View comments tab"
                                >
                                  Comments ({wo.comments?.length || wo.commentCount || 0})
                                </button>
                              </div>
                              
                              {/* Tab Content */}
                              {getActiveTab(wo.id) === 'details' ? (
                                /* Details Tab Content */
                                <div>
                                  {isEditMode ? (
                                    <textarea
                                      value={editedDescriptions[wo.id] ?? (wo.description ?? '')}
                                      onChange={(e) => handleDescriptionChange(wo.id, e.target.value)}
                                      onBlur={() => handleDescriptionBlur(wo)}
                                      onClick={(e) => e.stopPropagation()}
                                      rows={4}
                                      placeholder="Work order description..."
                                      className="w-full text-sm text-gray-700 bg-white p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 whitespace-pre-wrap"
                                      disabled={updatingWorkOrders.has(wo.id)}
                                    />
                                  ) : (
                                    <p className="text-sm text-gray-600 whitespace-pre-wrap bg-white p-3 rounded-lg border border-gray-200">
                                      {wo.description || 'No description provided'}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                /* Comments Tab Content */
                                <div 
                                  className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                                  style={{ maxHeight: '400px', overflowY: 'auto' }}
                                >
                                  {(!wo.comments || wo.comments.length === 0) ? (
                                    <div className="p-4 text-center text-gray-500">
                                      <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                      </svg>
                                      <p className="text-sm">No comments on this work order</p>
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-gray-100">
                                      {wo.comments.map((comment) => (
                                        <div 
                                          key={comment.id} 
                                          className={`p-3 ${comment.isEscalation ? 'bg-orange-50 border-l-4 border-orange-400' : ''}`}
                                        >
                                          {/* Comment Header */}
                                          <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm font-medium text-gray-900">
                                                {comment.userName || 'Unknown User'}
                                              </span>
                                              {comment.userRole && (
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                                  {comment.userRole.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
                                                </span>
                                              )}
                                              {comment.isEscalation && (
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium">
                                                  🔥 Urgent
                                                </span>
                                              )}
                                            </div>
                                            <span className="text-xs text-gray-500">
                                              {format(new Date(comment.createdAt), 'MMM dd, yyyy h:mm a')}
                                            </span>
                                          </div>
                                          
                                          {/* Comment Text */}
                                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                            {comment.comment}
                                          </p>
                                          
                                          {/* Attachments */}
                                          {comment.attachments && comment.attachments.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                              {comment.attachments.map((url, index) => (
                                                <a
                                                  key={index}
                                                  href={url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="block w-16 h-16 rounded overflow-hidden border border-gray-200 hover:border-purple-500 transition-colors"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <img
                                                    src={url}
                                                    alt={`Attachment ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                  />
                                                </a>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Expenses Section - show if has expenses OR in edit mode */}
                            {(woHasExpenses || isEditMode) && (
                              <>
                            <h4 className="text-sm font-semibold text-purple-800 mb-3">
                              Expenses ({wo.expenses?.length || 0})
                            </h4>
                            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Title
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Base Cost
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Billed Amount
                                    </th>
                                    {isEditMode && (
                                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                                        Status
                                      </th>
                                    )}
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Added By
                                    </th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      Date
                                    </th>
                                    {isEditMode && (
                                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                                        Actions
                                      </th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                  {wo.expenses?.map(expense => {
                                    const isSaving = savingExpenses.has(expense.id);
                                    const isModified = isExpenseModified(expense);
                                    const currentBaseCost = getExpenseValue(expense, 'baseCost');
                                    const currentBilledAmount = getExpenseValue(expense, 'billedAmount');
                                    
                                    return (
                                      <tr 
                                        key={expense.id} 
                                        className={`hover:bg-gray-50 ${isModified ? 'bg-yellow-50' : ''}`}
                                      >
                                        <td className="px-4 py-3 text-sm text-gray-900">
                                          {isEditMode ? (
                                            <input
                                              type="text"
                                              value={editedExpenses[expense.id]?.title ?? (expense.title ?? '')}
                                              onChange={(e) => handleExpenseChange(expense, 'title', e.target.value)}
                                              onBlur={() => handleExpenseBlur(expense)}
                                              onClick={(e) => e.stopPropagation()}
                                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                              placeholder="Expense title"
                                              disabled={isSaving}
                                            />
                                          ) : (
                                            expense.title
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                          {isEditMode ? (
                                            <div className="flex items-center">
                                              <span className="text-gray-400 mr-1">$</span>
                                              <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={editedExpenses[expense.id]?.baseCost ?? (currentBaseCost ?? '')}
                                                onChange={(e) => handleExpenseChange(expense, 'baseCost', e.target.value)}
                                                onBlur={() => handleExpenseBlur(expense)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                                placeholder="0.00"
                                                disabled={isSaving}
                                              />
                                            </div>
                                          ) : (
                                            currentBaseCost !== null ? formatCurrency(currentBaseCost) : '—'
                                          )}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-600">
                                          {isEditMode ? (
                                            <div className="flex items-center">
                                              <span className="text-gray-400 mr-1">$</span>
                                              <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={editedExpenses[expense.id]?.billedAmount ?? (currentBilledAmount ?? '')}
                                                onChange={(e) => handleExpenseChange(expense, 'billedAmount', e.target.value)}
                                                onBlur={() => handleExpenseBlur(expense)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                                placeholder="0.00"
                                                disabled={isSaving}
                                              />
                                            </div>
                                          ) : (
                                            currentBilledAmount !== null ? formatCurrency(currentBilledAmount) : '—'
                                          )}
                                        </td>
                                        {isEditMode && (
                                          <td className="px-4 py-3 text-sm text-center">
                                            {/* State-showing toggle: pill reflects the
                                                expense's CURRENT billable state (green =
                                                billable, red = unbillable). Click to flip. */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleExpenseUnbillable(expense);
                                              }}
                                              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                                                expense.isBillable === false
                                                  ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                                  : 'text-green-700 bg-green-100 hover:bg-green-200'
                                              }`}
                                              title={expense.isBillable === false ? 'Click to mark as billable' : 'Click to mark as unbillable'}
                                            >
                                              {expense.isBillable === false ? (
                                                <>
                                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                  </svg>
                                                  Unbillable
                                                </>
                                              ) : (
                                                <>
                                                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                  </svg>
                                                  Billable
                                                </>
                                              )}
                                            </button>
                                          </td>
                                        )}
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                          {expense.createdByName || 'Unknown'}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-500">
                                          {expense.createdAt ? format(new Date(expense.createdAt), 'MMM dd, yyyy') : '—'}
                                        </td>
                                        {isEditMode && (
                                          <td className="px-4 py-3 text-sm text-center">
                                            {isSaving ? (
                                              <svg className="animate-spin h-4 w-4 text-purple-600 mx-auto" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                              </svg>
                                            ) : (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteExpense(expense);
                                                }}
                                                className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                title="Delete expense"
                                                aria-label="Delete expense"
                                              >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                                                </svg>
                                              </button>
                                            )}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  })}
                                  
                                  {/* New Expense Row - Always show at bottom when in edit mode */}
                                  {isEditMode && (
                                    <tr className="bg-gray-50 border-t-2 border-purple-300">
                                      <td className="px-4 py-3">
                                        <input
                                          type="text"
                                          value={newExpenses[wo.id]?.title || ''}
                                          onChange={(e) => handleNewExpenseChange(wo.id, 'title', e.target.value)}
                                          onClick={(e) => e.stopPropagation()}
                                          placeholder="Enter expense title..."
                                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                          disabled={creatingExpenses.has(wo.id)}
                                        />
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="flex items-center">
                                          <span className="text-gray-400 mr-1">$</span>
                                          <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={newExpenses[wo.id]?.baseCost || ''}
                                            onChange={(e) => handleNewExpenseChange(wo.id, 'baseCost', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                            placeholder="0.00"
                                            disabled={creatingExpenses.has(wo.id)}
                                          />
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="flex items-center">
                                          <span className="text-gray-400 mr-1">$</span>
                                          <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={newExpenses[wo.id]?.billedAmount || ''}
                                            onChange={(e) => handleNewExpenseChange(wo.id, 'billedAmount', e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                            placeholder="0.00"
                                            disabled={creatingExpenses.has(wo.id)}
                                          />
                                        </div>
                                      </td>
                                      {isEditMode && (
                                        <td className="px-4 py-3 text-center">
                                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            Billable
                                          </span>
                                        </td>
                                      )}
                                      <td className="px-4 py-3 text-sm text-gray-500">
                                        {creatingExpenses.has(wo.id) ? (
                                          <span className="text-xs text-gray-400">Saving...</span>
                                        ) : (
                                          <span className="text-xs text-gray-400">—</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-500">
                                        <span className="text-xs text-gray-400">—</span>
                                      </td>
                                      {isEditMode && (
                                        <td className="px-4 py-3 text-center">
                                          {creatingExpenses.has(wo.id) ? (
                                            <svg className="animate-spin h-4 w-4 text-purple-600" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          ) : (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleCreateExpense(wo.id);
                                              }}
                                              disabled={!newExpenses[wo.id]?.title?.trim() || creatingExpenses.has(wo.id)}
                                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                              >
                                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Save
                                              </button>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                              </>
                            )}
                            
                            {/* Labor/Time Section - Show if time logged OR in edit mode */}
                            {(wo.totalTimeWorkedMinutes > 0 || isEditMode) && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-blue-800 flex items-center">
                                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Labor Charges
                                  </h4>
                                  {/* Billable status badge and toggle */}
                                  <div className="flex items-center gap-2">
                                    {wo.isLaborBillable ? (
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        Billable
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        Unbillable
                                      </span>
                                    )}
                                    {isEditMode && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleLaborBillable(wo);
                                        }}
                                        className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                          wo.isLaborBillable
                                            ? 'text-red-700 bg-red-100 hover:bg-red-200'
                                            : 'text-green-700 bg-green-100 hover:bg-green-200'
                                        }`}
                                      >
                                        {wo.isLaborBillable ? (
                                          <>
                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Mark Unbillable
                                          </>
                                        ) : (
                                          <>
                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Mark Billable
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <div className={`rounded-lg p-4 ${wo.isLaborBillable ? 'bg-green-50' : 'bg-blue-50'}`}>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                      <span className="text-gray-600">Time Logged:</span>
                                      {isEditMode ? (
                                        <div className="mt-1 flex items-center gap-2">
                                          <input
                                            type="number"
                                            min="0"
                                            max="999"
                                            defaultValue={Math.floor(wo.totalTimeWorkedMinutes / 60)}
                                            onBlur={(e) => {
                                              const minutesInput = e.target.parentElement?.querySelector('[data-time-field="minutes"]');
                                              const minutes = minutesInput ? parseInt(minutesInput.value) || 0 : wo.totalTimeWorkedMinutes % 60;
                                              handleTimeEdit(wo.id, e.target.value, minutes);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            data-time-field="hours"
                                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="0"
                                          />
                                          <span className="text-gray-500">h</span>
                                          <input
                                            type="number"
                                            min="0"
                                            max="59"
                                            defaultValue={wo.totalTimeWorkedMinutes % 60}
                                            onBlur={(e) => {
                                              const hoursInput = e.target.parentElement?.querySelector('[data-time-field="hours"]');
                                              const hours = hoursInput ? parseInt(hoursInput.value) || 0 : Math.floor(wo.totalTimeWorkedMinutes / 60);
                                              handleTimeEdit(wo.id, hours, e.target.value);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            data-time-field="minutes"
                                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="0"
                                          />
                                          <span className="text-gray-500">m</span>
                                        </div>
                                      ) : (
                                        <span className="ml-2 font-medium text-gray-900">
                                          {Math.floor(wo.totalTimeWorkedMinutes / 60)}h {wo.totalTimeWorkedMinutes % 60}m
                                        </span>
                                      )}
                                    </div>
                                    <div>
                                      <span className="text-gray-600">First Hour (Free):</span>
                                      <span className="ml-2 font-medium text-gray-900">1h</span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Billable Hours:</span>
                                      <span className="ml-2 font-medium text-gray-900">
                                        {wo.billableHours.toFixed(2)}h
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Labor Charge:</span>
                                      <span className={`ml-2 font-semibold ${wo.isLaborBillable ? 'text-green-700' : 'text-blue-700'}`}>
                                        {formatCurrency(wo.laborCharge)}
                                      </span>
                                    </div>
                                  </div>
                                  {wo.billableHours > 0 && summary?.serviceChargeHourlyRate > 0 && (
                                    <div className="mt-2 text-xs text-gray-500">
                                      Calculation: {wo.billableHours.toFixed(2)} hours × {formatCurrency(summary.serviceChargeHourlyRate)}/hr = {formatCurrency(wo.laborCharge)}
                                    </div>
                                  )}
                                  
                                  {/* Time entries detail */}
                                  {wo.timeEntries && wo.timeEntries.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-blue-200">
                                      <div className="text-xs font-medium text-gray-600 mb-2">Time Entries:</div>
                                      <div className="space-y-1">
                                        {wo.timeEntries.map(entry => (
                                          <div key={entry.id} className="flex justify-between text-xs text-gray-600">
                                            <span>{entry.workerName || 'Unknown'}</span>
                                            <span>{Math.floor(entry.durationMinutes / 60)}h {entry.durationMinutes % 60}m</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Service Charge Section */}
                            {(isEditMode || wo.hasServiceCharge) && defaultServiceCharge > 0 && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-semibold text-emerald-800 flex items-center">
                                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Service Charge
                                  </h4>
                                  <div className="flex items-center gap-2">
                                    {wo.hasServiceCharge ? (
                                      <>
                                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                          Applied
                                        </span>
                                        {isEditMode && (
                                          <div className="flex items-center gap-2">
                                            <div className="flex items-center">
                                              <span className="text-gray-400 mr-1">$</span>
                                              <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                defaultValue={wo.serviceCharge ?? ''}
                                                key={`sc-${wo.id}-${wo.serviceCharge}`}
                                                onChange={(e) => handleServiceChargeChange(wo.id, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                                placeholder="0.00"
                                              />
                                            </div>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleServiceCharge(wo);
                                              }}
                                              className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
                                            >
                                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                              </svg>
                                              Remove
                                            </button>
                                          </div>
                                        )}
                                        {!isEditMode && (
                                          <span className="text-sm font-semibold text-emerald-700">
                                            {formatCurrency(wo.serviceCharge)}
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      isEditMode && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleServiceCharge(wo);
                                          }}
                                          className="inline-flex items-center px-3 py-1 text-xs font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 rounded-md transition-colors"
                                        >
                                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                          </svg>
                                          Add Service Charge ({formatCurrency(defaultServiceCharge)})
                                        </button>
                                      )
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredAndSortedWorkOrders.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No work orders found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm
                ? 'Try adjusting your search term'
                : excludedCount > 0
                  ? 'All matching work orders have been removed from this report. Use Restore All to show them again.'
                  : 'No work orders match the selected filters'}
            </p>
          </div>
        )}
      </div>

      {/* Grand Totals Section - Bottom Left */}
      {filteredAndSortedWorkOrders.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6 max-w-md">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Report Summary</h3>
          <div className="space-y-1">
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">Total Work Orders:</span>
              <span className="text-xs font-semibold text-gray-900">{filteredAndSortedWorkOrders.length}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">Total Base Cost:</span>
              <span className="text-xs font-semibold text-gray-900">{formatCurrency(summary.grandTotalBaseCost)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">Total Expenses Billed:</span>
              <span className="text-xs font-semibold text-gray-900">
                {formatCurrency(summary.totalBilledExpenses || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-600">Total Expenses (Unbillable):</span>
              <span className="text-xs font-semibold text-gray-900">
                {formatCurrency(summary.totalUnbilledExpenses || 0)}
              </span>
            </div>
            
            {/* Total Labor - Billed first, then Unbilled */}
            {(summary.totalLaborCharge > 0 || summary.totalTimeWorkedMinutes > 0) && (
              <>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-xs font-medium text-gray-600">Total Labor Billed:</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {formatCurrency(summary.approvedLaborCharge || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-xs font-medium text-gray-600">Total Labor (Unbillable):</span>
                  <span className="text-xs font-semibold text-gray-900">
                    {formatCurrency((summary.totalLaborCharge || 0) - (summary.approvedLaborCharge || 0))}
                  </span>
                </div>
              </>
            )}
            
            {/* Total Unbilled and Total Billed */}
            <div className="flex justify-between items-center pt-6 pb-2 border-t-2 border-t-gray-300 mt-2">
              <span className="text-sm font-bold text-gray-700">Total Unbillable:</span>
              <span className="text-sm font-bold text-gray-900">
                {formatCurrency(summary.totalUnbilled || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm font-bold text-gray-700">Total Billed:</span>
              <span className="text-sm font-bold text-gray-900">
                {formatCurrency(summary.totalBilled || 0)}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-3 bg-gray-50 -mx-6 px-6 -mb-6 rounded-b-lg">
              <span className="text-base font-bold text-gray-900">Total Profit:</span>
              <span className={`text-lg font-bold ${(summary.grandTotalProfit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(summary.grandTotalProfit || 0)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* QuickBooks Invoice Modal */}
      <QuickBooksInvoiceModal
        isOpen={quickBooksModalOpen}
        onClose={() => setQuickBooksModalOpen(false)}
        workOrders={filteredAndSortedWorkOrders}
        onInvoiceCreated={handleQuickBooksInvoiceCreated}
      />

    </div>
  );
}

export default WorkOrderReportView;
